import { __awaiter } from "tslib";
import { Client, Protocol } from '@tencent/mega-ipc-renderer';
import { WEB_CHIMERA_API, } from '@tencent/mega-sdk-protocols';
class MegaPlayer {
    constructor(player) {
        /**
         * web端不直接操作player实例，通过events事件控制播放器
         * @param events 播放器实例事件管理
         * @returns
         */
        this.syncEvents = (events) => {
            // on事件监听
            this.registListenEvent(events);
            // emit事件触发
            this.registEmitEvent(events);
        };
        this.registListenEvent = (events) => {
            events.on("play" /* MegaPlayerEvent.Play */, (src) => {
                this.play(src);
            });
            events.on("pause" /* MegaPlayerEvent.Pause */, () => this.pause());
            events.on("stop" /* MegaPlayerEvent.Stop */, () => this.stop());
            events.on("close" /* MegaPlayerEvent.Close */, () => this.close());
            events.on("toggle-pause" /* MegaPlayerEvent.TogglePause */, () => this.togglePause());
            events.on("toggle-mute" /* MegaPlayerEvent.ToggleMute */, () => this.toggleMute());
            events.on("set-volume" /* MegaPlayerEvent.SetVolume */, (volume) => {
                this.volume = volume;
            });
            events.on("set-position" /* MegaPlayerEvent.SetPosition */, (position) => {
                this.position = position;
            });
            events.on("set-rate" /* MegaPlayerEvent.SetRate */, (rate) => {
                this.rate = rate;
            });
            events.on("set-time" /* MegaPlayerEvent.SetTime */, (time) => {
                this.time = time;
            });
            events.on("set-mute" /* MegaPlayerEvent.SetMute */, (mute) => {
                this.mute = mute;
            });
        };
        this.registEmitEvent = (events) => {
            this.on("frame-ready" /* MegaPlayerEvent.FrameReady */, (frame) => {
                events.emit("frame-ready" /* MegaPlayerEvent.FrameReady */, frame);
            });
            this.on("frame-setup" /* MegaPlayerEvent.FrameSetup */, (width, height) => {
                events.emit("frame-setup" /* MegaPlayerEvent.FrameSetup */, width, height);
            });
            this.on("time-changed" /* MegaPlayerEvent.TimeChanged */, (time) => {
                events.emit("time-changed" /* MegaPlayerEvent.TimeChanged */, time);
            });
            this.on("length-changed" /* MegaPlayerEvent.LengthChanged */, (length) => {
                events.emit("length-changed" /* MegaPlayerEvent.LengthChanged */, length);
            });
            this.on("position-changed" /* MegaPlayerEvent.PositionChanged */, (position) => {
                events.emit("position-changed" /* MegaPlayerEvent.PositionChanged */, position);
            });
            this.on("log-message" /* MegaPlayerEvent.LogMessage */, (level, message) => {
                events.emit("log-message" /* MegaPlayerEvent.LogMessage */, level, message);
            });
            this.on("encountered-error" /* MegaPlayerEvent.EncounteredError */, (...data) => events.emit("encountered-error" /* MegaPlayerEvent.EncounteredError */, ...data));
            this.on("opening" /* MegaPlayerEvent.Opening */, (...data) => events.emit("opening" /* MegaPlayerEvent.Opening */, ...data));
            this.on("playing" /* MegaPlayerEvent.Playing */, (...data) => events.emit("playing" /* MegaPlayerEvent.Playing */, ...data));
            this.on("paused" /* MegaPlayerEvent.Paused */, (...data) => events.emit("paused" /* MegaPlayerEvent.Paused */, ...data));
            this.on("stopped" /* MegaPlayerEvent.Stopped */, (...data) => events.emit("stopped" /* MegaPlayerEvent.Stopped */, ...data));
            this.on("forward" /* MegaPlayerEvent.Forward */, (...data) => events.emit("forward" /* MegaPlayerEvent.Forward */, ...data));
            this.on("backward" /* MegaPlayerEvent.Backward */, (...data) => events.emit("backward" /* MegaPlayerEvent.Backward */, ...data));
            this.on("buffering" /* MegaPlayerEvent.Buffering */, (...data) => events.emit("buffering" /* MegaPlayerEvent.Buffering */, ...data));
            this.on("end-reached" /* MegaPlayerEvent.EndReached */, (...data) => events.emit("end-reached" /* MegaPlayerEvent.EndReached */, ...data));
            this.on("frame-cleanup" /* MegaPlayerEvent.FrameCleanup */, (...data) => events.emit("frame-cleanup" /* MegaPlayerEvent.FrameCleanup */, ...data));
            this.on("media-changed" /* MegaPlayerEvent.MediaChanged */, (...data) => events.emit("media-changed" /* MegaPlayerEvent.MediaChanged */, ...data));
            this.on("nothing-special" /* MegaPlayerEvent.NothingSpecial */, (...data) => events.emit("nothing-special" /* MegaPlayerEvent.NothingSpecial */, ...data));
            this.on("pausable-changed" /* MegaPlayerEvent.PausableChanged */, (...data) => events.emit("pausable-changed" /* MegaPlayerEvent.PausableChanged */, ...data));
            this.on("seekable-changed" /* MegaPlayerEvent.SeekableChanged */, (...data) => events.emit("seekable-changed" /* MegaPlayerEvent.SeekableChanged */, ...data));
        };
        this.player = player;
    }
    static createPlayer() {
        return __awaiter(this, void 0, void 0, function* () {
            const WebChimera = yield this.WebChimeraAPI.requireWebChimera();
            return new this(WebChimera.createPlayer());
        });
    }
    static getPlayableSrcFromURI(uri) {
        return __awaiter(this, void 0, void 0, function* () {
            const { client } = this;
            if (yield client.media.isMediaURI(uri)) {
                return client.media.getLocationFromMediaURI(uri);
            }
            return uri;
        });
    }
    play(src) {
        if (src === undefined) {
            return this.player.play();
        }
        return this.player.play(src);
    }
    pause() {
        return this.player.pause();
    }
    togglePause() {
        return this.player.togglePause();
    }
    stop() {
        return this.player.stop();
    }
    toggleMute() {
        return this.player.toggleMute();
    }
    close() {
        return this.player.close();
    }
    set volume(volume) {
        this.player.volume = volume;
    }
    get volume() {
        return this.player.volume;
    }
    set position(position) {
        this.player.position = position;
    }
    get position() {
        return this.player.position;
    }
    set rate(rate) {
        this.player.input.rate = rate;
    }
    get rate() {
        return this.player.input.rate;
    }
    set time(time) {
        this.player.time = time;
    }
    get time() {
        return this.player.time;
    }
    set mute(mute) {
        this.player.mute = mute;
    }
    get mute() {
        return this.player.mute;
    }
    get playing() {
        return this.player.playing;
    }
    get state() {
        return this.player.state;
    }
    get length() {
        return this.player.length;
    }
    on(eventName, handler) {
        if (eventName === "frame-ready" /* MegaPlayerEvent.FrameReady */) {
            this.player.onFrameReady = handler;
            return;
        }
        if (eventName === "frame-setup" /* MegaPlayerEvent.FrameSetup */) {
            this.player.onFrameSetup = handler;
            return;
        }
        if (eventName === "time-changed" /* MegaPlayerEvent.TimeChanged */) {
            this.player.onTimeChanged = handler;
            return;
        }
        if (eventName === "length-changed" /* MegaPlayerEvent.LengthChanged */) {
            this.player.onLengthChanged = handler;
            return;
        }
        if (eventName === "position-changed" /* MegaPlayerEvent.PositionChanged */) {
            this.player.onPositionChanged = handler;
            return;
        }
        if (eventName === "opening" /* MegaPlayerEvent.Opening */) {
            this.player.onOpening = handler;
            return;
        }
        if (eventName === "playing" /* MegaPlayerEvent.Playing */) {
            this.player.onPlaying = handler;
            return;
        }
        if (eventName === "paused" /* MegaPlayerEvent.Paused */) {
            this.player.onPaused = handler;
            return;
        }
        if (eventName === "stopped" /* MegaPlayerEvent.Stopped */) {
            this.player.onStopped = handler;
            return;
        }
        if (eventName === "backward" /* MegaPlayerEvent.Backward */) {
            this.player.onBackward = handler;
            return;
        }
        if (eventName === "buffering" /* MegaPlayerEvent.Buffering */) {
            this.player.onBuffering = handler;
            return;
        }
        if (eventName === "encountered-error" /* MegaPlayerEvent.EncounteredError */) {
            this.player.onEncounteredError = handler;
            return;
        }
        if (eventName === "end-reached" /* MegaPlayerEvent.EndReached */) {
            this.player.onEndReached = handler;
            return;
        }
        if (eventName === "forward" /* MegaPlayerEvent.Forward */) {
            this.player.onForward = handler;
            return;
        }
        if (eventName === "frame-cleanup" /* MegaPlayerEvent.FrameCleanup */) {
            this.player.onFrameCleanup = handler;
            return;
        }
        if (eventName === "media-changed" /* MegaPlayerEvent.MediaChanged */) {
            this.player.onMediaChanged = handler;
            return;
        }
        if (eventName === "nothing-special" /* MegaPlayerEvent.NothingSpecial */) {
            this.player.onNothingSpecial = handler;
            return;
        }
        if (eventName === "pausable-changed" /* MegaPlayerEvent.PausableChanged */) {
            this.player.onPausableChanged = handler;
            return;
        }
        if (eventName === "seekable-changed" /* MegaPlayerEvent.SeekableChanged */) {
            this.player.onSeekableChanged = handler;
            return;
        }
        if (eventName === "log-message" /* MegaPlayerEvent.LogMessage */) {
            this.player.onLogMessage = handler;
            return;
        }
    }
}
MegaPlayer.WebChimeraAPI = window[WEB_CHIMERA_API];
MegaPlayer.client = Client.create({
    media: Protocol(),
});
export default MegaPlayer;
//# sourceMappingURL=mega-player.js.map